import React, { useMemo, useState } from 'react'
import type { ComponentType, ReactNode } from 'react'
import { Stack } from 'react-bootstrap'
import isBoolean from 'lodash/isBoolean'
import { HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { SaveCancelButtons } from '@ztechapps/webui.button'
import { generateId, parseAddress } from '@ztechapps/common.utils'
import { AddressInput } from './AddressInput'

type ExtraActionsArgs = {
  onAddressSelected: (address: any) => void
  submitting: boolean
}

export type AddressSelectEditContentProps = {
  value: any
  setNewValue: (value: any) => void
  /** Resolves `true` only on a genuine successful save — the underlying `useInlineEditor.saveNow`
   * this always wraps never rejects (it swallows and toasts its own error), so a caller cannot
   * tell success from failure by whether the promise resolves; it must report it explicitly. */
  saveNow: () => Promise<boolean>
  cancelNow: () => any
  submitting: boolean
  /** The save's last error — the popover arm passes it so the card names why it stays open, beside Save, where the
   * anchored field's own error line sits under the card. */
  error?: string
  addressSelectComponent: ComponentType<any>
  addressSelectProps?: Record<string, any>
  buildFilter?: (showAllAddress: boolean) => Record<string, any>
  extraActions?: (args: ExtraActionsArgs) => ReactNode
  /** Transform input string into address object. Shared component handles setNewValue and showing the form. */
  parseCreateOption?: (inputValue: string) => any
}

const hasAddressData = (value: any) => {
  return !!(value?.city || value?.street1 || value?.name)
}

export const AddressSelectEditContent = ({
  value,
  setNewValue,
  saveNow,
  cancelNow,
  submitting,
  error,
  addressSelectComponent: AddressSelectComponent,
  addressSelectProps,
  buildFilter,
  extraActions,
  parseCreateOption,
}: AddressSelectEditContentProps) => {
  const [showAddressEdit, setShowAddressEdit] = useState(() => hasAddressData(value))
  const [showAllAddress, setShowAllAddress] = useState<boolean>(false)

  const filter = useMemo(() => {
    if (buildFilter) return buildFilter(showAllAddress)
    return addressSelectProps?.filter
  }, [buildFilter, showAllAddress, addressSelectProps?.filter])

  const handleAddressSelected = (address: any) => {
    setNewValue(address)
    setShowAddressEdit(true)
  }

  return (
    <Stack gap={2}>
      <HorizontalStack gap={1}>
        <div className="flex-grow-1">
          <AddressSelectComponent
            {...addressSelectProps}
            // An empty picker has nothing to clear; only offer the clear button once a
            // value is actually selected.
            isClearable={hasAddressData(value)}
            value={value}
            filter={filter}
            showAll={showAllAddress}
            autoFocus
            onChange={(e: any) => {
              if (isBoolean(e?.showAll)) {
                setShowAllAddress(!showAllAddress)
                setShowAddressEdit(false)
              } else {
                setNewValue(e)
                setShowAddressEdit(true)
              }
            }}
            disabled={submitting}
            placeholder="Type here to add or choose an address"
            onCreateOption={(inputValue: string) => {
              const address = parseCreateOption
                ? parseCreateOption(inputValue)
                : { ...parseAddress(inputValue), _id: generateId() }
              setNewValue(address)
              setShowAddressEdit(true)
            }}
          />
        </div>
        {extraActions?.({ onAddressSelected: handleAddressSelected, submitting })}
      </HorizontalStack>
      <div className="small">Choose the address you want to use or edit.</div>
      <ShowIf condition={showAddressEdit}>
        <div>
          <hr />
          <AddressInput
            value={value}
            disabled={submitting}
            includeStreet3
            includeAttention
            includePhone
            includeCompany
            onChange={(e) => {
              setNewValue({ ...e, _id: value?._id ?? generateId() })
            }}
          />
        </div>
      </ShowIf>
      <ShowIf condition={!!error}>
        <div className="small text-danger" role="alert">
          {error}
        </div>
      </ShowIf>
      {/* Save and Cancel close the editor, below the fields, bottom-right — the customer editor's placement. The AI
          wand stays beside the picker. */}
      <HorizontalStack className="justify-content-end border-top pt-2">
        {/* No hover tooltip: the shared one opens beside the button and covers its sibling. */}
        <SaveCancelButtons
          saveTitle=""
          cancelTitle=""
          isBusy={submitting}
          onSave={async () => {
            // Collapse only once the save genuinely succeeds — `saveNow` still runs (and any
            // error still toasts/renders) either way, but a blocked save must leave the fields
            // that the error message names on screen, not hide them.
            const succeeded = await saveNow()
            if (succeeded) {
              setShowAddressEdit(false)
            }
          }}
          onCancel={() => {
            setShowAddressEdit(false)
            return cancelNow()
          }}
        />
      </HorizontalStack>
    </Stack>
  )
}
